<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { dataHojeBR, formatarDataBR } from '@/utils/useDataBR'
import { FIN_ST_MOV_BANC } from '@/constants/FIN_ST_MOV_BANC'
import { MOE_FONTE_COTACAO } from '@/constants/MOE_FONTE_COTACAO'
import { ST } from '@/constants/ST'
import { FIN_ST_PLAN_CATEGFIN } from '@/constants/FIN_ST_PLAN_CATEGFIN'
import { FIN_ST_TAX_CONVERCAO } from '@/constants/FIN_ST_TAX_CONVERCAO'
import { criarConfigBuscaCategoriaFinanceira } from '@/search-definitions/categoriaFinanceiraSearch'

import { criarConfigBuscaContaBancaria } from '@/search-definitions/contaBancariaBuscaSearch'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  configBuscaContratoCambio: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idMovimentacaoBancaria: 0,
      idOrganizacao: null,
      idContaBancaria: null,
      idTituloFinanceiro: null,
      idContratoCambio: null,
      lstTipoMovimentacao: '',
      lstMovimento: '',
      lstFormaPagamento: '',
      lstStatusMovBancaria: FIN_ST_MOV_BANC.Criada,
      dataMovimento: dataHojeBR(),
      dataConciliacao: null,
      dataEstorno: null,
      lstMoedaTitulo: '',
      valorAbatidoTitulo: null,
      valorDescontoTitulo: null,
      lstMoedaBanco: '',
      valorEfetivoMoedaBanco: null,
      taxaBancoPadrao: null,
      taxaBancoGerencial: null,
      taxaCambioPadraoTitulo: null,
      lstMoedaPadrao: '',
      taxaTituloPadrao: null,
      taxaTituloPadraoCobrada: null,
      dataReferenciaCambioPadrao: null,
      lstFonteCambioPadrao: '',
      lstTaxaCambioStatusPadrao: FIN_ST_TAX_CONVERCAO.Definitiva,
      dataReferenciaCambioGerencial: null,
      lstFonteCambioGerencial: '',
      lstTaxaCambioStatusGerencial: FIN_ST_TAX_CONVERCAO.Definitiva,
      lstTipoTaxaConversao: '',
      valorEfetivoMoedaPadrao: null,
      valorVariacaoCambialPadrao: null,
      lstSinalVariacaoCambialPadrao: '',
      valorVariacaoSpreadPadrao: null,
      lstSinalVariacaoSpreadPadrao: '',
      lstMoedaGerencial: '',
      taxaTituloGerencial: null,
      taxaCambioGerencialPadrao: null,
      valorEfetivoMoedaGerencial: null,
      valorVariacaoCambialGerencial: null,
      lstSinalVariacaoCambialGerencial: '',
      valorVariacaoSpreadGerencial: null,
      lstSinalVariacaoSpreadGerencial: '',
      lstMotivoEstorno: '',
      justificativaEstorno: '',
      codigoTransacaoBanco: '',
      observacao: '',
      nomeContaBancaria: ''
    })
  }
})

const emit = defineEmits(['salvo', 'cancelado', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

const isLoadingListas = ref(false)

const accordionState = reactive({
  capa: true,
  valores: true,
  cambio: false,
  conciliacao: false
})

const listas = reactive({})

// Status options para CampoSelect
const statusOptions = [
  { id: FIN_ST_MOV_BANC.Criada, label: 'Criada' },
  { id: FIN_ST_MOV_BANC.Baixada, label: 'Baixada' },
  { id: FIN_ST_MOV_BANC.Conciliada, label: 'Conciliada' },
  { id: FIN_ST_MOV_BANC.Estornada, label: 'Estornada' }
]

// --- Composable Base ---
const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const tituloModulo = computed(() => {
  return 'Movimentação Bancária'
})

const temPermissaoCompleta = computed(() =>
  authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
)
const isReadOnly = computed(() => isEditing.value && !temPermissaoCompleta.value)

const isBlqueadoPorStatus = computed(() => {
  if (!isEditing.value) return false
  return item.value.lstStatusMovBancaria === FIN_ST_MOV_BANC.Conciliada ||
         item.value.lstStatusMovBancaria === FIN_ST_MOV_BANC.Estornada
})

const isBlqueadoPorTitulo = computed(() => {
  if (!isEditing.value) return false
  return !item.value.idTituloFinanceiro
})

const podeEstornar = computed(() => {
  if (!isEditing.value || isReadOnly.value) return false
  return item.value.lstStatusMovBancaria === FIN_ST_MOV_BANC.Baixada
})

const entityInfoCalculado = computed(() => {
  if (!isEditing.value) return entityInfo.value

  const partes = []
  if (item.value.nomeContaBancaria) {
    partes.push(item.value.nomeContaBancaria)
  }
  if (item.value.dataMovimento) {
    partes.push(formatarDataBR(item.value.dataMovimento))
  }
  return {
    id: entityInfo.value?.id || item.value.idMovimentacaoBancaria,
    name: partes.join(' - ') || 'Nova Movimentação',
    nameLabel: 'Movimentação'
  }
})

// --- Configuração de Busca de Conta Bancária ---
const configBuscaConta = computed(() => criarConfigBuscaContaBancaria(authStore.organizacaoAtiva))

// --- Plano de categoria financeira vigente para filtro da busca ---
const idPlanoCategoriaFinanceira = ref(null)

const resolverPlanoCategoria = async () => {
  try {
    const res = await api.get('/PlanosCategoriasFinanceiras', {
      params: {
        lstStatusPlano: FIN_ST_PLAN_CATEGFIN.Ativo,
        idEmpresa: authStore.empresaAtiva,
        pageSize: 50
      }
    })
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const plano = (res.data || []).find(p => {
      if (!p.dataInicioVigencia || !p.dataFimVigencia) return false
      const inicio = new Date(p.dataInicioVigencia + 'T00:00:00')
      const fim = new Date(p.dataFimVigencia + 'T00:00:00')
      return hoje >= inicio && hoje <= fim
    })
    idPlanoCategoriaFinanceira.value = plano ? plano.idPlanoCategoriaFinanceira : null
  } catch (e) {
    console.warn('[MovimentacoesBancariasCadastro] Erro ao resolver plano vigente:', e)
    idPlanoCategoriaFinanceira.value = null
  }
}

const configBuscaCategoria = ref(null)

const atualizarConfigBuscaCategoria = () => {
  if (!idPlanoCategoriaFinanceira.value) {
    configBuscaCategoria.value = null
    return
  }
  configBuscaCategoria.value = criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira',
    null,
    ST.Ativo,
    authStore.organizacaoAtiva,
    null,
    idPlanoCategoriaFinanceira.value
  )
}

watch([() => authStore.organizacaoAtiva, idPlanoCategoriaFinanceira], atualizarConfigBuscaCategoria, { immediate: true })

// --- Carregamento de Listas ---
const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['MovimentacaoBancaria'])
    Object.assign(listas, result)

    // Carrega FIN_FORMAS_PG se não veio pelos metadados
    if (!listas.LstFormaPagamento) {
      try {
        listas.LstFormaPagamento = await metadataStore.getListaLookup('FIN_FORMAS_PG', 'Sistema')
      } catch (e) { console.warn('FIN_FORMAS_PG não encontrada') }
    }

    // Carrega FIN_MOTIV_ESTORNO (lista não estrutural)
    if (!listas.LstMotivoEstorno) {
      try {
        listas.LstMotivoEstorno = await metadataStore.getListaLookup('FIN_MOTIV_ESTORNO', 'Cliente')
      } catch (e) { console.warn('FIN_MOTIV_ESTORNO não encontrada') }
    }
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados auxiliares.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

// --- Labels dinâmicas para taxas ---
const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const idStr = String(codigoMoeda)
  const itemLista = (listas.LstMoeda || []).find(m =>
    String(m.idListaSistemaItem) === idStr ||
    String(m.idListaClienteItem) === idStr
  )
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

const labelTaxaBanco = computed(() => {
  const siglaBanco = obterSiglaMoeda(item.value.lstMoedaBanco)
  const siglaTitulo = obterSiglaMoeda(item.value.lstMoedaTitulo)
  return `Taxa Banco ${siglaBanco}/${siglaTitulo}`
})

const labelTaxaEmissao = computed(() => {
  const siglaTitulo = obterSiglaMoeda(item.value.lstMoedaTitulo)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa Emissão ${siglaTitulo}/${siglaPadrao}`
})

const labelTaxaPadrao = computed(() => {
  const siglaTitulo = obterSiglaMoeda(item.value.lstMoedaTitulo)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `PTAX ${siglaTitulo}/${siglaPadrao}`
})

const labelTaxaGerencial = computed(() => {
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  return `PTAX ${siglaPadrao}/${siglaGerencial}`
})

const labelTaxaGerencialPadrao = computed(() => {
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  const siglaPadrao = obterSiglaMoeda(item.value.lstMoedaPadrao)
  return `Taxa ${siglaGerencial}/${siglaPadrao}`
})

const labelTaxaCambioGerencial = computed(() => {
  const siglaTitulo = obterSiglaMoeda(item.value.lstMoedaTitulo)
  const siglaGerencial = obterSiglaMoeda(item.value.lstMoedaGerencial)
  return `Taxa ${siglaTitulo}/${siglaGerencial}`
})

// --- MODAL DE COTAÇÃO MANUAL ---
const isModalCotacaoManualOpen = ref(false)

const cotacaoManualData = reactive({
  taxaCambioPadrao: null,
  taxaCambioGerencialPadrao: null,
  taxaCambioGerencial: null
})

watch(
  () => [cotacaoManualData.taxaCambioPadrao, cotacaoManualData.taxaCambioGerencialPadrao],
  ([taxaPadrao, taxaGerencialPadrao]) => {
    if (taxaPadrao && taxaGerencialPadrao && Number(taxaGerencialPadrao) !== 0) {
      cotacaoManualData.taxaCambioGerencial = Math.round(
        (Number(taxaPadrao) / Number(taxaGerencialPadrao)) * 1e6
      ) / 1e6
    }
  }
)

const abrirModalCotacaoManual = () => {
  cotacaoManualData.taxaCambioPadrao = item.value.taxaTituloPadrao
  cotacaoManualData.taxaCambioGerencialPadrao = item.value.taxaCambioGerencialPadrao
  cotacaoManualData.taxaCambioGerencial = item.value.taxaTituloGerencial
  isModalCotacaoManualOpen.value = true
}

const salvarCotacaoManual = async () => {
  item.value.taxaTituloPadrao = cotacaoManualData.taxaCambioPadrao
  item.value.taxaCambioGerencialPadrao = cotacaoManualData.taxaCambioGerencialPadrao
  item.value.taxaTituloGerencial = cotacaoManualData.taxaCambioGerencial
  item.value.lstFonteCambioPadrao = MOE_FONTE_COTACAO.Manual
  item.value.lstFonteCambioGerencial = MOE_FONTE_COTACAO.Manual
  item.value.lstTaxaCambioStatusPadrao = FIN_ST_TAX_CONVERCAO.Definitiva
  item.value.lstTaxaCambioStatusGerencial = FIN_ST_TAX_CONVERCAO.Definitiva

  const valorEfetivo = Number(item.value.valorEfetivoMoedaBanco) || 0
  const taxaPadrao = Number(item.value.taxaTituloPadrao) || 0
  const taxaGerencial = Number(item.value.taxaTituloGerencial) || 0
  item.value.valorEfetivoMoedaPadrao = taxaPadrao > 0 ? Math.round(valorEfetivo * taxaPadrao * 100) / 100 : 0
  item.value.valorEfetivoMoedaGerencial = taxaGerencial > 0 ? Math.round(valorEfetivo * taxaGerencial * 100) / 100 : null

  if (item.value.idMovimentacaoBancaria) {
    try {
      const payload = { ...item.value }
      await api.put(`/MovimentacoesBancarias/${item.value.idMovimentacaoBancaria}`, payload)
      emit('show-notification', { message: 'Taxas de câmbio atualizadas com sucesso.', type: 'success' })
    } catch (error) {
      emit('show-notification', { message: 'Erro ao salvar taxas de câmbio.', type: 'error' })
    }
  }
  isModalCotacaoManualOpen.value = false
}

// --- RESOLUÇÃO AUTOMÁTICA DE TAXAS ---
const isResolvendoTaxas = ref(false)

const mostrarBotaoDefinirTaxa = computed(() => {
  if (!isEditing.value) return false
  return true
})

const podeDefinirTaxaManual = computed(() => {
  if (!isEditing.value) return false
  return !!item.value.idContaBancaria
})

const podeDefinirTaxaAutomatica = computed(() => {
  if (!isEditing.value) return false
  return !!item.value.idContaBancaria && !!item.value.idMovimentacaoBancaria
})

const resolverTaxasAutomaticamente = async () => {
  if (!item.value.idMovimentacaoBancaria) {
    emit('show-notification', { message: 'Salve a movimentação antes de resolver as taxas automaticamente.', type: 'warning' })
    return
  }
  isResolvendoTaxas.value = true
  try {
    const response = await api.post(`/MovimentacoesBancarias/${item.value.idMovimentacaoBancaria}/resolver-taxas`)
    const dados = response.data
    item.value.lstMoedaPadrao = dados.lstMoedaPadrao
    item.value.taxaTituloPadrao = dados.taxaTituloPadrao
    item.value.lstTaxaCambioStatusPadrao = dados.lstTaxaCambioStatusPadrao
    item.value.dataReferenciaCambioPadrao = dados.dataReferenciaCambioPadrao
    item.value.lstFonteCambioPadrao = dados.lstFonteCambioPadrao
    item.value.lstMoedaGerencial = dados.lstMoedaGerencial
    item.value.taxaTituloGerencial = dados.taxaTituloGerencial
    item.value.taxaCambioGerencialPadrao = dados.taxaCambioGerencialPadrao
    item.value.valorEfetivoMoedaPadrao = dados.valorEfetivoMoedaPadrao
    item.value.valorEfetivoMoedaGerencial = dados.valorEfetivoMoedaGerencial
    item.value.taxaBancoPadrao = dados.taxaBancoPadrao
    item.value.taxaBancoGerencial = dados.taxaBancoGerencial
    emit('show-notification', { message: 'Taxas de câmbio resolvidas automaticamente com sucesso.', type: 'success' })
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao resolver taxas de câmbio automaticamente.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isResolvendoTaxas.value = false
  }
}

// --- MODAL DE ESTORNO ---
const isModalEstornoOpen = ref(false)
const modalEstornoLoading = ref(false)

const dadosEstorno = reactive({
  dataEstorno: dataHojeBR(),
  lstMotivoEstorno: '',
  justificativaEstorno: ''
})

const abrirModalEstorno = () => {
  dadosEstorno.dataEstorno = dataHojeBR()
  dadosEstorno.lstMotivoEstorno = ''
  dadosEstorno.justificativaEstorno = ''
  isModalEstornoOpen.value = true
}

const confirmarEstorno = async () => {
  if (!dadosEstorno.lstMotivoEstorno) {
    emit('show-notification', { message: 'O motivo do estorno é obrigatório.', type: 'warning' })
    return
  }

  modalEstornoLoading.value = true
  try {
    await api.delete(`/MovimentacoesBancarias/${item.value.idMovimentacaoBancaria}`, {
      params: { motivo: dadosEstorno.lstMotivoEstorno }
    })
    item.value.lstStatusMovBancaria = FIN_ST_MOV_BANC.Estornada
    item.value.dataEstorno = dadosEstorno.dataEstorno
    item.value.lstMotivoEstorno = dadosEstorno.lstMotivoEstorno
    item.value.justificativaEstorno = dadosEstorno.justificativaEstorno
    emit('show-notification', { message: 'Estorno realizado com sucesso.', type: 'success' })
    isModalEstornoOpen.value = false
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao processar estorno.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    modalEstornoLoading.value = false
  }
}

// --- ITENS DA COMPOSIÇÃO DO MOVIMENTO (MovimentacaoBancariaItem) ---
const itensBaixa = ref([])
const isModalItemOpen = ref(false)
const indiceItemEditando = ref(-1)

const novoItemModelo = {
  idCategoriaFinanceira: null,
  nomeCategoriaFinanceira: '',
  codigoEstruturado: '',
  valorMoedaBanco: null,
  valorMoedaPadrao: 0,
  valorMoedaGerencial: null,
  descricaoComplementar: ''
}

const modalItemParaEdicao = reactive({ ...novoItemModelo })

const totalItensMoedaBanco = computed(() => {
  return itensBaixa.value.reduce((acc, cur) => acc + (Number(cur.valorMoedaBanco) || 0), 0)
})

const totalItensMoedaPadrao = computed(() => {
  return itensBaixa.value.reduce((acc, cur) => acc + (Number(cur.valorMoedaPadrao) || 0), 0)
})

const totalItensMoedaGerencial = computed(() => {
  return itensBaixa.value.reduce((acc, cur) => acc + (Number(cur.valorMoedaGerencial) || 0), 0)
})

const percentualComposicao = computed(() => {
  const total = Number(item.value.valorEfetivoMoedaBanco) || 0
  if (total <= 0) return 0
  return Math.round((totalItensMoedaBanco.value / total) * 100)
})

const excedeValorMovimentado = computed(() => {
  const totalMovimentado = Number(item.value.valorEfetivoMoedaBanco) || 0
  return totalItensMoedaBanco.value > totalMovimentado
})

const abrirModalItem = (index = -1) => {
  atualizarConfigBuscaCategoria()
  if (index === -1) {
    indiceItemEditando.value = -1
    Object.assign(modalItemParaEdicao, novoItemModelo)
  } else {
    indiceItemEditando.value = index
    Object.assign(modalItemParaEdicao, itensBaixa.value[index])
  }
  isModalItemOpen.value = true
}

const salvarModalItem = () => {
  if (!modalItemParaEdicao.idCategoriaFinanceira) {
    emit('show-notification', { message: 'Categoria Financeira é obrigatória.', type: 'warning' })
    return
  }
  if (!modalItemParaEdicao.valorMoedaBanco || modalItemParaEdicao.valorMoedaBanco <= 0) {
    emit('show-notification', { message: 'O Valor deve ser maior que zero.', type: 'warning' })
    return
  }

  const totalAposLancamento = totalItensMoedaBanco.value + Number(modalItemParaEdicao.valorMoedaBanco)
  if (indiceItemEditando.value === -1 && totalAposLancamento > Number(item.value.valorEfetivoMoedaBanco)) {
    emit('show-notification', {
      message: `O valor total (${totalAposLancamento.toFixed(2)}) excede o Valor Movimentado (${Number(item.value.valorEfetivoMoedaBanco).toFixed(2)}).`,
      type: 'warning'
    })
    return
  }

  const payloadItem = { ...modalItemParaEdicao }
  if (indiceItemEditando.value === -1) {
    itensBaixa.value.push(payloadItem)
  } else {
    itensBaixa.value.splice(indiceItemEditando.value, 1, payloadItem)
  }
  isModalItemOpen.value = false
}

const removerItem = (index) => {
  itensBaixa.value.splice(index, 1)
}

const replicarValorRestante = () => {
  const total = Number(item.value.valorEfetivoMoedaBanco) || 0
  const usado = totalItensMoedaBanco.value
  const restante = Math.max(0, total - usado)
  modalItemParaEdicao.valorMoedaBanco = restante > 0 ? restante : total
}

// --- Ações Customizadas ---
const submitFormCustom = async () => {
  if (isReadOnly.value) return

  // Validações
  if (!item.value.idContaBancaria) {
    emit('show-notification', { message: 'Conta Bancária é obrigatória.', type: 'warning' })
    return false
  }
  if (!item.value.lstTipoMovimentacao) {
    emit('show-notification', { message: 'Tipo de Movimentação é obrigatório.', type: 'warning' })
    return false
  }
  if (!item.value.lstMovimento) {
    emit('show-notification', { message: 'O Movimento (Débito/Crédito) é obrigatório.', type: 'warning' })
    return false
  }
  if (!item.value.dataMovimento) {
    emit('show-notification', { message: 'Data do Movimento é obrigatória.', type: 'warning' })
    return false
  }
  if (!item.value.valorEfetivoMoedaBanco || Number(item.value.valorEfetivoMoedaBanco) <= 0) {
    emit('show-notification', { message: 'O Valor Efetivo deve ser maior que zero.', type: 'warning' })
    return false
  }

  await submitForm('MovimentacoesBancarias', 'idMovimentacaoBancaria')
}

const handleCancelCustom = () => {
  handleCancel()
}

// --- Inicialização ---
onMounted(async () => {
  await carregarListas()
  await resolverPlanoCategoria()
})

const formatarNumeroView = (valor, decimais = 2) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

</script>

<template>
  <div class="movimentacao-bancaria-form-wrapper">
  <FormularioCadastro
    :module-name="tituloModulo"
    :is-editing="isEditing"
    :is-loading="isLoading || isLoadingListas"
    :entity-info="entityInfoCalculado"
    :desabilitar-salvar="isReadOnly"
    :status="item.lstStatusMovBancaria"
    :accordion-state="accordionState"
    @submit="submitFormCustom"
    @cancelar="handleCancelCustom"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
      <button
        v-if="podeEstornar"
        type="button"
        class="btn btn-outline-danger btn-sm"
        @click="abrirModalEstorno"
        title="Estornar Movimentação"
      >
        <i class="bi bi-arrow-counterclockwise me-1"></i> Estornar
      </button>
    </template>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- FRAME 1: CAPA DA OPERAÇÃO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.capa = !accordionState.capa">
        <h5 class="mb-0"><i class="bi bi-bank me-2 text-primary"></i> Capa da Operação</h5>
        <i :class="['bi', accordionState.capa ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.capa">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoBusca
              id="idContaBancaria"
              label="Conta Bancária Origem/Destino"
              v-model="item.idContaBancaria"
              :configuracao-busca="configBuscaConta"
              required
              :disabled="isReadOnly || isBlqueadoPorStatus"
              @selecionado="val => {
                item.nomeContaBancaria = val.descricao
                item.lstMoedaBanco = val.lstMoeda
              }"
              @show-notification="p => emit('show-notification', p)"
            />
          </div>

          <div class="col-md-2">
            <CampoInput
              id="idTituloFinanceiro"
              label="ID Título Financeiro"
              type="text"
              v-model="item.idTituloFinanceiro"
              :readonly="isEditing"
              :disabled="isReadOnly"
              descricao-informativa="Deixe vazio para Tarifas Bancárias avulsas."
            />
          </div>

          <div class="col-md-2">
            <CampoInput id="dataMovimento" label="Data do Movimento" type="date" v-model="item.dataMovimento" required :disabled="isReadOnly || isBlqueadoPorStatus" />
          </div>

          <div class="col-md-2">
            <CampoInput
              id="valorEfetivoMoedaBanco"
              label="Valor Efetivo"
              type="moeda"
              v-model="item.valorEfetivoMoedaBanco"
              required
              :disabled="isReadOnly || isBlqueadoPorStatus"
              descricao-informativa="Valor que efetivamente saiu/entrou na conta."
            />
          </div>

          <div class="col-md-4">
            <CampoBuscaLista
              id="lstTipoMovimentacao"
              label="Tipo de Movimentação"
              v-model="item.lstTipoMovimentacao"
              tabela="MovimentacaoBancaria"
              required
              :disabled="isReadOnly || isBlqueadoPorStatus"
            />
          </div>

          <div class="col-md-2">
            <CampoBuscaLista
              id="lstMovimento"
              label="Movimento (D/C)"
              v-model="item.lstMovimento"
              tabela="MovimentacaoBancaria"
              required
              :disabled="isReadOnly || isBlqueadoPorStatus"
            />
          </div>

          <div class="col-md-4">
            <CampoBuscaLista
              id="lstFormaPagamento"
              label="Forma de Pagamento"
              v-model="item.lstFormaPagamento"
              tabela="MovimentacaoBancaria"
              :disabled="isReadOnly || isBlqueadoPorStatus"
            />
          </div>

          <div class="col-md-4">
            <CampoSelect
              id="lstStatusMovBancaria"
              label="Status da Movimentação"
              v-model="item.lstStatusMovBancaria"
              :options="statusOptions"
              option-label="label"
              option-value="id"
              :disabled="true"
              descricao-informativa="Gerido automaticamente pelo sistema."
            />
          </div>

          <div class="col-md-4">
            <CampoBusca
              id="idContratoCambio"
              label="Contrato de Câmbio"
              v-model="item.idContratoCambio"
              :configuracao-busca="configBuscaContratoCambio"
              :disabled="true"
              descricao-informativa="Auto-preenchido pelo sistema quando o título está vinculado a um contrato de câmbio via CambioLiquidacao."
              @show-notification="p => emit('show-notification', p)"
            />
          </div>

        </div>
      </div>
    </div>

    <!-- FRAME 2: VALORES DA OPERAÇÃO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.valores = !accordionState.valores">
        <h5 class="mb-0"><i class="bi bi-currency-exchange me-2 text-primary"></i> Valores da Operação</h5>
        <i :class="['bi', accordionState.valores ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.valores">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoInput
              id="lstMoedaBanco"
              label="Moeda da Conta Bancária"
              type="text"
              v-model="item.lstMoedaBanco"
              disabled
              descricao-informativa="Preenchido automaticamente ao selecionar a conta."
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="taxaBancoPadrao"
              :label="labelTaxaBanco"
              type="moeda"
              :casasDecimais="4"
              v-model="item.taxaBancoPadrao"
              required
              disabled
              descricao-informativa="Taxa praticada pelo banco (com spread)."
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="taxaBancoGerencial"
              label="Taxa Banco Gerencial"
              type="moeda"
              :casasDecimais="4"
              v-model="item.taxaBancoGerencial"
              disabled
              descricao-informativa="Taxa de conversão banco/gerencial."
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME: VALORES VINCULADOS AO TÍTULO -->
    <div class="card mb-4" v-if="isEditing">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="bi bi-link-45deg me-2 text-primary"></i> Valores Vinculados ao Título</h5>
        <i class="bi bi-info-circle text-secondary" title="Campos liberados apenas quando há título vinculado."></i>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoBuscaLista
              id="lstMoedaTitulo"
              label="Moeda do Título"
              v-model="item.lstMoedaTitulo"
              tabela="MovimentacaoBancaria"
              required
              :disabled="isReadOnly || isBlqueadoPorStatus || isBlqueadoPorTitulo"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="valorAbatidoTitulo"
              label="Valor Abatido (Moeda Título)"
              type="moeda"
              v-model="item.valorAbatidoTitulo"
              required
              :disabled="isReadOnly || isBlqueadoPorStatus || isBlqueadoPorTitulo"
              descricao-informativa="Valor baixado na moeda original do título."
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="valorDescontoTitulo"
              label="Desconto (Moeda Título)"
              type="moeda"
              v-model="item.valorDescontoTitulo"
              :disabled="isReadOnly || isBlqueadoPorStatus || isBlqueadoPorTitulo"
              descricao-informativa="Valor de desconto obtido na negociação."
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="taxaCambioPadraoTitulo"
              :label="labelTaxaEmissao"
              type="moeda"
              :casasDecimais="4"
              v-model="item.taxaCambioPadraoTitulo"
              required
              :disabled="isReadOnly || isBlqueadoPorStatus || isBlqueadoPorTitulo"
              descricao-informativa="Taxa original do título na emissão."
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME: COMPOSIÇÃO DO MOVIMENTO -->
    <div class="card mb-4 border-primary">
      <div class="card-header bg-primary text-white cursor-pointer d-flex justify-content-between align-items-center">
        <h5 class="mb-0 text-white"><i class="bi bi-list-check me-2"></i> Composição do Movimento</h5>
        <div class="d-flex align-items-center gap-3">
          <div class="d-flex align-items-center gap-2">
            <div class="progress flex-grow-1" style="height: 8px; width: 120px;">
              <div class="progress-bar" role="progressbar"
                :style="{ width: Math.min(100, percentualComposicao) + '%' }"
                :class="[
                  excedeValorMovimentado ? 'bg-danger' : '',
                  !excedeValorMovimentado && percentualComposicao >= 100 ? 'bg-success' : '',
                  !excedeValorMovimentado && percentualComposicao < 100 ? 'bg-warning' : ''
                ]">
              </div>
            </div>
            <small class="text-white fw-semibold">{{ percentualComposicao }}%</small>
          </div>
          <button type="button" class="btn btn-sm btn-light py-0"
            :disabled="!item.idContaBancaria || isReadOnly || isBlqueadoPorStatus"
            @click.stop="abrirModalItem(-1)">
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>
      <div class="card-body border-top border-secondary-subtle p-0">
        <div class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th>Categoria Financeira</th>
                <th>Descrição</th>
                <th class="text-end">Valor (Moeda Banco)</th>
                <th class="text-end">Valor (Moeda Padrão)</th>
                <th class="text-end">Valor (Moeda Gerencial)</th>
                <th class="text-end" style="width:80px;" v-if="!isReadOnly && !isBlqueadoPorStatus">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(linha, index) in itensBaixa" :key="index">
                <td class="fw-medium">{{ linha.nomeCategoriaFinanceira || '-' }}</td>
                <td><small>{{ linha.descricaoComplementar || '-' }}</small></td>
                <td class="text-end fw-bold">{{ formatarNumeroView(linha.valorMoedaBanco) }}</td>
                <td class="text-end">{{ formatarNumeroView(linha.valorMoedaPadrao) }}</td>
                <td class="text-end">{{ linha.valorMoedaGerencial ? formatarNumeroView(linha.valorMoedaGerencial) : '-' }}</td>
                <td class="text-end" v-if="!isReadOnly && !isBlqueadoPorStatus">
                  <button type="button" class="btn-icon text-primary" @click="abrirModalItem(index)"><i class="bi bi-pencil-fill"></i></button>
                  <button type="button" class="btn-icon text-danger" @click="removerItem(index)"><i class="bi bi-trash-fill"></i></button>
                </td>
              </tr>
              <tr v-if="itensBaixa.length === 0">
                <td :colspan="isReadOnly || isBlqueadoPorStatus ? 5 : 6" class="text-center text-muted py-4">
                  Nenhum item na Composição do Movimento. Clique em <i class="bi bi-plus-lg"></i> para adicionar itens.
                </td>
              </tr>
            </tbody>
            <tfoot v-if="itensBaixa.length > 0">
              <tr class="table-active fw-bold">
                <td colspan="2" class="text-end">TOTAIS:</td>
                <td class="text-end">{{ formatarNumeroView(totalItensMoedaBanco) }}</td>
                <td class="text-end">{{ formatarNumeroView(totalItensMoedaPadrao) }}</td>
                <td class="text-end">{{ totalItensMoedaGerencial ? formatarNumeroView(totalItensMoedaGerencial) : '-' }}</td>
                <td v-if="!isReadOnly && !isBlqueadoPorStatus"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- FRAME 3: CÂMBIO E CONVERSÃO -->
    <div class="card mb-4" v-if="isEditing">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.cambio = !accordionState.cambio">
        <h5 class="mb-0"><i class="bi bi-currency-exchange me-2 text-primary"></i> Câmbio e Conversão</h5>
        <i :class="['bi', accordionState.cambio ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.cambio">
        <div class="row g-3 mb-4 pb-3 border-bottom">
          <div class="col-md-3">
            <CampoBuscaLista id="lstTipoTaxaConversao" label="Tipo Taxa Conversão" v-model="item.lstTipoTaxaConversao" tabela="MovimentacaoBancaria" :disabled="true" descricao-informativa="Campos de conversão são gerados automaticamente" />
          </div>
          <div class="col-md-2">
            <CampoInput id="dataReferenciaCambioPadrao" label="Data Ref. Câmbio" type="date" v-model="item.dataReferenciaCambioPadrao" :disabled="true" />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista id="lstFonteCambioPadrao" label="Fonte da Cotação" v-model="item.lstFonteCambioPadrao" tabela="MovimentacaoBancaria" :disabled="true" />
          </div>
          <div class="col-md-4">
            <CampoBuscaLista id="lstTaxaCambioStatusPadrao" label="Status Taxa" v-model="item.lstTaxaCambioStatusPadrao" tabela="MovimentacaoBancaria" :disabled="true" />
          </div>
        </div>
        <div class="row g-3">
          <div class="col-md-6">
            <div class="card border-primary h-100">
              <div class="card-header bg-primary text-white py-2 d-flex justify-content-between align-items-center">
                <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Padrão</h6>
                <div class="d-flex gap-1">
                  <button
                    v-if="mostrarBotaoDefinirTaxa"
                    type="button"
                    class="btn btn-sm btn-outline-light py-0"
                    :disabled="!podeDefinirTaxaAutomatica || isResolvendoTaxas"
                    @click.stop="resolverTaxasAutomaticamente"
                    title="Buscar cotações automaticamente"
                  >
                    <i class="bi bi-robot me-1"></i> Definir Automaticamente
                  </button>
                  <button
                    v-if="mostrarBotaoDefinirTaxa"
                    type="button"
                    class="btn btn-sm btn-light py-0"
                    :disabled="!podeDefinirTaxaManual"
                    @click.stop="abrirModalCotacaoManual"
                    title="Definir manualmente as taxas de câmbio"
                  >
                    <i class="bi bi-pencil-square me-1"></i> Definir Manualmente
                  </button>
                </div>
              </div>
              <div class="card-body py-2">
                <div class="row g-2">
                  <div class="col-4">
                    <CampoBuscaLista id="lstMoedaPadrao" label="Moeda" v-model="item.lstMoedaPadrao" tabela="MovimentacaoBancaria" disabled />
                  </div>
                  <div class="col-4">
                    <CampoInput id="taxaTituloPadrao" :label="labelTaxaPadrao" type="moeda" :casasDecimais="4" v-model="item.taxaTituloPadrao" disabled />
                  </div>
                  <div class="col-4">
                    <CampoInput id="valorEfetivoMoedaPadrao" label="Valor Total" type="moeda" v-model="item.valorEfetivoMoedaPadrao" disabled />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card border-primary h-100">
              <div class="card-header bg-primary text-white py-2">
                <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Gerencial</h6>
              </div>
              <div class="card-body py-2">
                <div class="row g-2">
                  <div class="col-4">
                    <CampoBuscaLista id="lstMoedaGerencial" label="Moeda" v-model="item.lstMoedaGerencial" tabela="MovimentacaoBancaria" disabled />
                  </div>
                  <div class="col-4">
                    <CampoInput id="taxaCambioGerencialPadrao" :label="labelTaxaGerencialPadrao" type="moeda" :casasDecimais="4" v-model="item.taxaCambioGerencialPadrao" disabled />
                  </div>
                  <div class="col-4">
                    <CampoInput id="taxaTituloGerencial" :label="labelTaxaGerencial" type="moeda" :casasDecimais="4" v-model="item.taxaTituloGerencial" disabled />
                  </div>
                  <div class="col-4">
                    <CampoInput id="valorEfetivoMoedaGerencial" label="Valor Total" type="moeda" v-model="item.valorEfetivoMoedaGerencial" disabled />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 5: OBSERVAÇÕES E CONCILIAÇÃO -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.conciliacao = !accordionState.conciliacao">
        <h5 class="mb-0"><i class="bi bi-check2-square me-2 text-primary"></i> Observações e Conciliação</h5>
        <i :class="['bi', accordionState.conciliacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.conciliacao">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="observacao"
              label="Observações"
              type="textarea"
              v-model="item.observacao"
              :disabled="isReadOnly || isBlqueadoPorStatus"
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="codigoTransacaoBanco"
              label="Código Transação Bancária"
              v-model="item.codigoTransacaoBanco"
              :disabled="isReadOnly || isBlqueadoPorStatus"
              descricao-informativa="Código de identificação da transação no extrato bancário."
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="dataConciliacao"
              label="Data de Conciliação"
              type="date"
              v-model="item.dataConciliacao"
              :disabled="isReadOnly"
              descricao-informativa="Preenchido após conciliar OFX/RET."
            />
          </div>

          <div class="col-md-3" v-if="item.dataEstorno">
            <CampoInput
              id="dataEstorno"
              label="Data de Estorno"
              type="date"
              v-model="item.dataEstorno"
              disabled
            />
          </div>
        </div>
      </div>
    </div>

  </FormularioCadastro>

  <!-- MODAL DE ESTORNO -->
  <ModalFormulario
    v-model="isModalEstornoOpen"
    titulo="Estornar Movimentação Bancária"
    largura="modal-md"
    textoConfirmar="Confirmar Estorno"
    :loading="modalEstornoLoading"
    @confirmar="confirmarEstorno"
  >
    <div class="alert alert-warning border-0 rounded-3 mb-3">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      O estorno irá reverter o saldo devedor do título vinculado e marcar a movimentação como <strong>Estornada</strong>.
    </div>
    <div class="row g-3">
      <div class="col-md-6">
        <CampoInput
          id="modalDataEstorno"
          label="Data do Estorno"
          type="date"
          v-model="dadosEstorno.dataEstorno"
          required
        />
      </div>
      <div class="col-md-6">
        <CampoSelect
          id="modalLstMotivoEstorno"
          label="Motivo do Estorno"
          v-model="dadosEstorno.lstMotivoEstorno"
          :options="listas.LstMotivoEstorno || []"
          option-label="textoLista"
          option-value="idListaClienteItem"
          required
        />
      </div>
      <div class="col-md-12">
        <CampoInput
          id="modalJustificativaEstorno"
          label="Justificativa Detalhada"
          type="textarea"
          v-model="dadosEstorno.justificativaEstorno"
          placeholder="Descreva o motivo do estorno em detalhes..."
        />
      </div>
    </div>
  </ModalFormulario>

  <!-- MODAL DE COTAÇÃO MANUAL -->
  <ModalFormulario
    v-model="isModalCotacaoManualOpen"
    titulo="Definir Taxas de Câmbio Manualmente"
    tamanho="xs"
    textoConfirmar="Salvar Taxas"
    @confirmar="salvarCotacaoManual"
  >
    <div class="d-flex flex-column gap-2">
      <CampoInput id="modalTaxaCambioPadrao"
        :label="labelTaxaPadrao"
        type="moeda" :casasDecimais="4"
        v-model="cotacaoManualData.taxaCambioPadrao" />
      <CampoInput id="modalTaxaCambioGerencialPadrao"
        :label="labelTaxaGerencialPadrao"
        type="moeda" :casasDecimais="4"
        v-model="cotacaoManualData.taxaCambioGerencialPadrao" />
      <CampoInput id="modalTaxaCambioGerencial"
        :label="labelTaxaCambioGerencial"
        type="moeda" :casasDecimais="4"
        :model-value="cotacaoManualData.taxaCambioGerencial" disabled />
    </div>
    <div class="mt-2 text-muted small">
      <i class="bi bi-info-circle me-1"></i>
      O campo "{{ labelTaxaCambioGerencial }}" é calculado automaticamente:
      {{ labelTaxaPadrao }} &divide; {{ labelTaxaGerencialPadrao }}
    </div>
  </ModalFormulario>

  <!-- MODAL PARA ADICIONAR/EDITAR ITEM DA COMPOSIÇÃO DO MOVIMENTO -->
  <ModalFormulario
    v-model="isModalItemOpen"
    :titulo="indiceItemEditando === -1 ? 'Adicionar Item à Composição' : 'Editar Item da Composição'"
    largura="modal-md"
    textoConfirmar="Salvar Item"
    @confirmar="salvarModalItem"
  >
    <div class="row g-3">
      <div class="col-md-12">
        <CampoBusca id="idCategoriaFinanceira" label="Categoria Financeira (DRE)"
          v-model="modalItemParaEdicao.idCategoriaFinanceira"
          :configuracao-busca="configBuscaCategoria"
          required
          @selecionado="val => { modalItemParaEdicao.nomeCategoriaFinanceira = val.nome; modalItemParaEdicao.codigoEstruturado = val.codigoEstruturado }"
          @show-notification="p => emit('show-notification', p)" />
      </div>
      <div class="col-md-4">
        <div class="input-group-custom">
          <CampoInput id="modalValorRateio" label="Valor (Moeda Banco)"
            type="moeda" v-model="modalItemParaEdicao.valorMoedaBanco"
            required hide-label class="input-group-field" />
          <button type="button" class="btn btn-outline-secondary btn-replicar input-group-btn"
            title="Replicar valor restante"
            @click="replicarValorRestante">
            <i class="bi bi-arrow-repeat me-1"></i> Restante
          </button>
        </div>
      </div>
      <div class="col-md-8">
        <CampoInput id="descricaoComplementar" label="Descrição Complementar (Opcional)"
          type="text" v-model="modalItemParaEdicao.descricaoComplementar"
          placeholder="Ex: Tarifa, IOF, Serviço..." />
      </div>
    </div>
  </ModalFormulario>

  <!-- Componente de Visualização do Histórico -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="MovimentacaoBancaria"
    :chave-primaria="item.idMovimentacaoBancaria"
    :titulo-registro="`Movimentação ${item.idMovimentacaoBancaria}`"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</div>
</template>

<style scoped>
/* ============================================================ */
/* TEMA CUSTOM: Azul Escuro Acinzentado (#34495E)               */
/* ============================================================ */
.movimentacao-bancaria-form-wrapper {
  --ct-34495e: #34495E;
  --ct-34495e-border: rgba(52, 73, 94, 0.25);
  --ct-34495e-bg-subtle: rgba(52, 73, 94, 0.06);
  --ct-34495e-bg-subtle-dark: rgba(52, 73, 94, 0.2);
  --ct-34495e-text-emphasis: #2c3e50;
  --ct-34495e-text-emphasis-dark: #8ab4d6;
}

.border-ct {
  border-color: var(--ct-34495e-border) !important;
}

.bg-ct-subtle {
  background-color: var(--ct-34495e-bg-subtle) !important;
}

.text-ct-emphasis {
  color: var(--ct-34495e-text-emphasis) !important;
}

[data-bs-theme="dark"] .bg-ct-subtle {
  background-color: var(--ct-34495e-bg-subtle-dark) !important;
}

[data-bs-theme="dark"] .text-ct-emphasis {
  color: var(--ct-34495e-text-emphasis-dark) !important;
}

[data-bs-theme="dark"] .border-ct {
  border-color: rgba(138, 180, 214, 0.3) !important;
}
</style>
